// Shared time-picker helpers for the Order v2 schedule step.
//
// The schedule UI offers times on a 15-minute grid (96 slots, 12:00 AM →
// 11:45 PM). Admins can also type a custom time; `snapToOption` rounds any
// reasonable input to the nearest grid slot so the rest of the system
// (end-time auto-advance, duration/break math) keeps working on the grid.

const pad2 = (n: number) => String(n).padStart(2, '0');

const minutesToLabel = (total: number) => {
  const h24 = Math.floor(total / 60);
  const mn = total % 60;
  const ap = h24 < 12 ? 'AM' : 'PM';
  const h12 = h24 % 12 || 12;
  return `${h12}:${pad2(mn)} ${ap}`;
};

export const TIME_OPTIONS_15: string[] = Array.from({ length: 96 }, (_, i) =>
  minutesToLabel(i * 15),
);

const LAST_SLOT_MINUTES = 95 * 15; // 11:45 PM

export const indexOfOption = (label: string): number =>
  TIME_OPTIONS_15.indexOf(label);

// Parse a free-typed time into total minutes since midnight, or null if it
// can't be understood. Accepts "6", "6:07", "6:07 am", "18:30", "6 PM", with
// flexible casing/spacing.
const parseToMinutes = (input: string): number | null => {
  const m = input
    .trim()
    .toUpperCase()
    .match(/^(\d{1,2})(?::(\d{1,2}))?\s*(AM|PM|A|P)?$/);
  if (!m) return null;

  let h = parseInt(m[1], 10);
  const mn = m[2] != null ? parseInt(m[2], 10) : 0;
  const ap = m[3] ? m[3][0] : null; // 'A' | 'P' | null

  if (mn > 59) return null;

  if (ap) {
    // 12-hour clock
    if (h < 1 || h > 12) return null;
    if (ap === 'P' && h !== 12) h += 12;
    if (ap === 'A' && h === 12) h = 0;
  } else {
    // 24-hour clock
    if (h > 23) return null;
  }

  return h * 60 + mn;
};

// Round a typed value to the nearest 15-min grid slot and return its label,
// or null if the input is unparseable. Values that round past the last slot
// (e.g. 11:53 PM) clamp to 11:45 PM so the result is always on the grid.
export const snapToOption = (input: string): string | null => {
  const total = parseToMinutes(input);
  if (total == null) return null;
  const snapped = Math.min(Math.round(total / 15) * 15, LAST_SLOT_MINUTES);
  return TIME_OPTIONS_15[snapped / 15];
};
